// Dark-theme color conversion (2026-09-30).
//
// The app has ~6,000 hardcoded colors across ~275 files, so dark mode is not a
// palette swap: every color is converted where it is emitted instead (the Emotion
// plugin for sx/styled/MUI styles, the inline-style observer for style={{}} and
// SVG attributes). This module is the one place that decides what a light-theme
// color becomes in the dark theme.
//
// The target look is X's "Lights out" theme: pure black, every gray moved onto
// one barely-cool family (page and cards #000000, fills #16181C / #202327, rules
// #2F3336, secondary text #71767B, text #E7E9EA). What a color becomes depends
// on the ROLE it plays:
//
//   bg      light surfaces go black (cards stay black and keep their rule, like X's
//           columns); light gray fills become the dark fills; pastel callouts
//           (red-50, amber-100...) keep their hue as a dark tint; faint black
//           overlays become faint light ones. Saturated colors stay.
//   fg      dark text goes light; colored text is lifted until it reads on black.
//   border  light rules and faint black rules become the #2F3336 rule.
//   svg     SVG fill/stroke: light fills and grid lines go dark, dark labels light,
//           saturated data colors are kept.
//   shadow  only light "ring" shadows change; real shadows stay dark.
//
// Pure and cached; safe to call per declaration.

export type ColorRole = 'bg' | 'fg' | 'border' | 'svg' | 'shadow';

interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

const NAMED: Record<string, [number, number, number]> = {
  white: [255, 255, 255],
  black: [0, 0, 0],
  gray: [128, 128, 128],
  grey: [128, 128, 128],
  silver: [192, 192, 192],
  lightgray: [211, 211, 211],
  lightgrey: [211, 211, 211],
  gainsboro: [220, 220, 220],
  whitesmoke: [245, 245, 245],
  darkgray: [169, 169, 169],
  darkgrey: [169, 169, 169],
  dimgray: [105, 105, 105],
  dimgrey: [105, 105, 105],
  snow: [255, 250, 250],
  ivory: [255, 255, 240],
  ghostwhite: [248, 248, 255],
  aliceblue: [240, 248, 255],
};

// Below this chroma (max - min of the 0..1 channels) a color reads as gray.
const NEUTRAL_CHROMA = 0.12;

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

function parseChannel(raw: string, isAlpha: boolean): number {
  const s = raw.trim();
  if (s.endsWith('%')) {
    const pct = parseFloat(s);
    return isAlpha ? pct / 100 : (pct / 100) * 255;
  }
  return parseFloat(s);
}

export function parseColor(token: string): Rgba | null {
  const t = token.trim();
  const lower = t.toLowerCase();
  if (lower.startsWith('#')) {
    const hex = lower.slice(1);
    if (!/^[0-9a-f]+$/.test(hex)) return null;
    if (hex.length === 3 || hex.length === 4) {
      const [r, g, b, a] = hex.split('').map((c) => parseInt(c + c, 16));
      return { r, g, b, a: hex.length === 4 ? a / 255 : 1 };
    }
    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
      return { r, g, b, a };
    }
    return null;
  }
  const fn = lower.match(/^(rgba?|hsla?)\((.*)\)$/);
  if (fn) {
    const parts = fn[2].replace('/', ' ').split(/[\s,]+/).filter(Boolean);
    if (parts.length < 3) return null;
    if (fn[1].startsWith('rgb')) {
      const r = parseChannel(parts[0], false);
      const g = parseChannel(parts[1], false);
      const b = parseChannel(parts[2], false);
      const a = parts[3] !== undefined ? parseChannel(parts[3], true) : 1;
      if ([r, g, b, a].some((n) => Number.isNaN(n))) return null;
      return { r, g, b, a: clamp01(a) };
    }
    let h = parseFloat(parts[0]);
    if (parts[0].endsWith('turn')) h *= 360;
    else if (parts[0].endsWith('rad')) h = (h * 180) / Math.PI;
    const s = parseFloat(parts[1]) / 100;
    const l = parseFloat(parts[2]) / 100;
    const a = parts[3] !== undefined ? parseChannel(parts[3], true) : 1;
    if ([h, s, l, a].some((n) => Number.isNaN(n))) return null;
    const [r, g, b] = hslToRgb(((h % 360) + 360) % 360 / 360, clamp01(s), clamp01(l));
    return { r, g, b, a: clamp01(a) };
  }
  const named = NAMED[lower];
  return named ? { r: named[0], g: named[1], b: named[2], a: 1 } : null;
}

function rgbToHsl(r: number, g: number, b: number) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const chroma = max - min;
  let h = 0;
  let s = 0;
  if (chroma !== 0) {
    s = l > 0.5 ? chroma / (2 - max - min) : chroma / (max + min);
    if (max === rn) h = (gn - bn) / chroma + (gn < bn ? 6 : 0);
    else if (max === gn) h = (bn - rn) / chroma + 2;
    else h = (rn - gn) / chroma + 4;
    h /= 6;
  }
  return { h, s, l, chroma };
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const v = l * 255;
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t: number) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return [hue(h + 1 / 3) * 255, hue(h) * 255, hue(h - 1 / 3) * 255];
}

export function formatColor(c: Rgba): string {
  const r = Math.round(c.r);
  const g = Math.round(c.g);
  const b = Math.round(c.b);
  if (c.a >= 1) {
    return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
  }
  return `rgba(${r}, ${g}, ${b}, ${Math.round(c.a * 1000) / 1000})`;
}

const sameColor = (x: Rgba, y: Rgba) =>
  Math.round(x.r) === Math.round(y.r) &&
  Math.round(x.g) === Math.round(y.g) &&
  Math.round(x.b) === Math.round(y.b) &&
  Math.abs(x.a - y.a) < 0.001;

/** X's "Lights out" palette, the family every gray lands on. */
export const DARK = {
  page: '#000000',
  card: '#000000',
  fill: '#16181c',
  fillStrong: '#202327',
  rule: '#2f3336',
  textSecondary: '#71767b',
  text: '#e7e9ea',
} as const;

const NEUTRAL_HUE = 210 / 360;
// Lights out's grays are barely cool: ~0.12 saturation on the dark fills,
// fading to ~0.04 on text.
const neutralSat = (l: number) => Math.min(0.12, Math.max(0.04, 0.12 - (l - 0.1) * 0.2));
const SECONDARY_TEXT = { r: 113, g: 118, b: 123 };
const PRIMARY_TEXT = { r: 231, g: 233, b: 234 };
// theme.palette.background.default (src/theme.ts) is the page; it maps onto pure black.
const LIGHT_PAGE = { r: 238, g: 240, b: 255 };

// Piecewise-linear lookup over stops sorted by x.
function lookup(stops: Array<[number, number]>, x: number): number {
  if (x <= stops[0][0]) return stops[0][1];
  for (let i = 1; i < stops.length; i++) {
    const [x1, y1] = stops[i];
    if (x <= x1) {
      const [x0, y0] = stops[i - 1];
      return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
    }
  }
  return stops[stops.length - 1][1];
}

// Surfaces, by the light theme's lightness: white -> black (the card),
// #F9FAFB -> 0.045, #F3F4F6 -> 0.09 (#16181C), #E5E7EB -> 0.14 (#202327),
// #D1D5DB -> 0.2, and mid grays settle around 0.3-0.4.
const SURFACE_STOPS: Array<[number, number]> = [
  [0.45, 0.4],
  [0.65, 0.3],
  [0.84, 0.2],
  [0.91, 0.14],
  [0.96, 0.09],
  [0.98, 0.045],
  [1, 0],
];
// Pastel callouts (red-50, amber-100, indigo-50...) become dark tints of their hue.
const pastelLightness = (l: number) => 0.12 + (1 - l) * 0.3;
// Rules: #E5E7EB -> 0.2 (#2F3336), #D1D5DB -> 0.24.
const ruleLightness = (l: number) => 0.15 + (1 - l) * 0.55;
// Text, by the light theme's lightness: #111827 -> 0.91 (#E7E9EA), #374151 -> 0.8,
// #4B5563 -> 0.69, #6B7280 -> 0.47 (#71767B), #9CA3AF -> 0.4. Monotonic, so a
// muted label never comes out brighter than a secondary one.
const FG_STOPS: Array<[number, number]> = [
  [0, 0.93],
  [0.12, 0.91],
  [0.22, 0.86],
  [0.3, 0.76],
  [0.46, 0.47],
  [0.65, 0.4],
  [0.75, 0.36],
];
const fgLightness = (l: number) => lookup(FG_STOPS, l);
// Dark colored text/borders are lifted into 0.62..0.8 and desaturated a little
// (#007D00 green would otherwise come out neon).
const liftColored = (l: number) => Math.min(0.8, Math.max(0.62, 1 - l * 0.75));
const LIFT_MAX_S = 0.7;

// WCAG relative luminance. Colored text needs MIN_TEXT_LUMINANCE to read on
// black (5:1) and the #16181C fill (~4.2:1): brand indigo #4A46FF is ~3.6:1 on
// black until lifted.
function luminance(r: number, g: number, b: number): number {
  const f = (v: number) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
const MIN_TEXT_LUMINANCE = 0.2;

export function darkColor(c: Rgba, role: ColorRole): Rgba {
  if (c.a === 0) return c;
  const { h, s, l, chroma } = rgbToHsl(c.r, c.g, c.b);
  // A gray, including the cool Tailwind grays and slate. Very light colors also
  // need a low saturation: red-50 and indigo-50 have little chroma, but they are
  // pastel callouts, not grays, and keep their hue.
  const gray = chroma < NEUTRAL_CHROMA && (l <= 0.85 || s < 0.5);
  const tint = (nl: number, maxS: number): Rgba => {
    const [r, g, b] = hslToRgb(h, Math.min(s, maxS), clamp01(nl));
    return { r, g, b, a: c.a };
  };
  const neutral = (nl: number): Rgba => {
    const L = clamp01(nl);
    const [r, g, b] = hslToRgb(NEUTRAL_HUE, neutralSat(L), L);
    return { r, g, b, a: c.a };
  };
  // Colored text keeps its hue and only gets lighter, until it reads on black.
  const readable = (nl: number, maxS: number): Rgba => {
    const sat = Math.min(s, maxS);
    let L = clamp01(nl);
    let [r, g, b] = hslToRgb(h, sat, L);
    while (luminance(r, g, b) < MIN_TEXT_LUMINANCE && L < 0.85) {
      L = Math.min(0.85, L + 0.02);
      [r, g, b] = hslToRgb(h, sat, L);
    }
    return { r, g, b, a: c.a };
  };
  // Faint black overlays and rules (MUI hover, chips, dividers, the Paper border)
  // become faint light ones, so they stay visible on black.
  const faintLight = (alpha: number): Rgba => ({ ...PRIMARY_TEXT, a: alpha });

  switch (role) {
    case 'bg':
      if (c.a < 0.3) return gray && l < 0.5 ? faintLight(Math.min(0.3, c.a * 1.5)) : c;
      if (c.r === LIGHT_PAGE.r && c.g === LIGHT_PAGE.g && c.b === LIGHT_PAGE.b) return { r: 0, g: 0, b: 0, a: c.a };
      // Dark grays (tooltips, dark panels) keep their depth and take the neutral hue.
      if (gray) return neutral(l >= 0.45 ? lookup(SURFACE_STOPS, l) : l);
      if (l > 0.85) return tint(pastelLightness(l), 0.45);
      if (l > 0.7) return tint(0.2 + (0.85 - l) * 0.8, 0.6);
      return c;
    case 'fg':
      if (gray) {
        // Light text was already sitting on a dark surface.
        if (l > 0.75) return c;
        // Translucent dark text is MUI's text scale: 0.87 is primary, 0.6 and
        // below secondary/disabled, which Lights out renders as #71767B.
        if (c.a < 1 && l < 0.5) {
          return c.a >= 0.75 ? { ...PRIMARY_TEXT, a: c.a } : { ...SECONDARY_TEXT, a: Math.min(1, c.a / 0.6) };
        }
        return neutral(fgLightness(l));
      }
      return l < 0.55 ? readable(liftColored(l), LIFT_MAX_S) : readable(l, 1);
    case 'border':
      if (c.a < 0.3) return gray && l < 0.5 ? faintLight(Math.min(0.22, c.a * 2.5)) : c;
      if (gray) return neutral(l >= 0.5 ? ruleLightness(l) : 0.8 - l * 0.6);
      if (l > 0.85) return tint(ruleLightness(l) + 0.02, 0.45);
      return l < 0.4 ? tint(liftColored(l), Math.min(s, LIFT_MAX_S)) : c;
    case 'svg':
      if (gray) {
        if (l >= 0.97) return neutral(lookup(SURFACE_STOPS, l));
        if (l > 0.75) return neutral(ruleLightness(l));
        if (l < 0.45) return neutral(fgLightness(l));
        return neutral(l);
      }
      return l > 0.85 ? tint(pastelLightness(l), 0.45) : c;
    case 'shadow':
      return gray && l > 0.6 && c.a >= 0.3 ? neutral(ruleLightness(l)) : c;
    default:
      return c;
  }
}

// Hex, rgb()/rgba(), hsl()/hsla(), or a bare word (checked against NAMED).
const TOKEN = /#[0-9a-fA-F]{3,8}\b|(?:rgba?|hsla?)\([^()]*\)|[a-zA-Z][a-zA-Z-]*/g;
const cache = new Map<string, string>();
const CACHE_LIMIT = 20000;

/**
 * Converts every color inside a CSS value ("1px solid #E5E7EB",
 * "linear-gradient(#fff, #f5f5f5)", "var(--x, #fff)") for the given role.
 * Values with url() are left alone (SVG paint servers, images).
 */
export function darkenCssValue(value: string, role: ColorRole): string {
  if (!value || value.indexOf('url(') !== -1) return value;
  const key = `${role}|${value}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  const out = value.replace(TOKEN, (tok: string, offset: number) => {
    // A word glued to "-" or another word char is part of an identifier
    // (--common-white, currentColor, transparent), never a color.
    if (/^[a-z]/i.test(tok) && offset > 0 && /[-\w]/.test(value[offset - 1])) return tok;
    const c = parseColor(tok);
    if (!c) return tok;
    const d = darkColor(c, role);
    return d === c || sameColor(d, c) ? tok : formatColor(d);
  });
  if (cache.size > CACHE_LIMIT) cache.clear();
  cache.set(key, out);
  return out;
}

const BORDER_PROP = /^(border|outline|column-rule)(-(top|right|bottom|left|block|inline)(-(start|end))?)?(-color)?$/;
const PROP_ROLE: Record<string, ColorRole> = {
  color: 'fg',
  'caret-color': 'fg',
  '-webkit-text-fill-color': 'fg',
  'text-decoration': 'fg',
  'text-decoration-color': 'fg',
  background: 'bg',
  'background-color': 'bg',
  'background-image': 'bg',
  fill: 'svg',
  stroke: 'svg',
  'stop-color': 'svg',
  'flood-color': 'svg',
  'box-shadow': 'shadow',
  '-webkit-box-shadow': 'shadow',
};

/** The role a CSS property's colors play, or null when the property is left alone. */
export function roleForProperty(prop: string): ColorRole | null {
  const p = prop.toLowerCase();
  if (PROP_ROLE[p]) return PROP_ROLE[p];
  return BORDER_PROP.test(p) ? 'border' : null;
}
